import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useReplicate } from './ReplicateContext';
import { ReplicatePropertyCheckbox } from './ReplicatePropertyCheckbox';
import { updateElementPropertyWithUndo } from './utils/undoRedo/actionHelpers';
import { ChevronDown, ChevronRight, X, Check } from 'lucide-react';
import URLInputWithLibrary from './URLInputWithLibrary';
import FlowSelector from './FlowSelector';
import FontDropdown from './FontDropdown';

// Default values for all possible element properties
const ELEMENT_PROPERTY_DEFAULTS: { [key: string]: any } = {
    // Layout properties
    horizontalLocation: 'center',
    verticalLocation: 'center',
    groupLocation: undefined,
    order: 0,
    layer: 0,

    // Position properties
    horizontalOffset: 0,
    verticalOffset: 0,
    horizontalPadding: 0,
    verticalPadding: 0,
    horizontalMargin: 0,
    verticalMargin: 0,

    // Appearance properties
    width: undefined,
    height: undefined,
    backgroundColor: undefined,
    borderColor: undefined,
    borderWidth: 0,
    borderRadius: 0,
    cornerRadius: 0,
    opacity: 1,

    // Typography properties
    fontSize: 16,
    fontWeight: 'normal',
    fontFamily: 'system-ui',
    fontFace: 'system',
    textColor: '#000000',
    textAlign: 'center',
    alignment: 'center',
    hideWhenKeyboardVisible: true,

    // Content properties
    text: '',
    placeholder: '',
    defaultValue: '',
    imageUrl: '',
    videoUrl: '',
    scene: '',
    name: '',

    // Behavior properties
    isRequired: false,
    isDisabled: false,
    isHidden: false,
    isFocused: false,
    useImageDimensions: false,

    // Button-specific properties
    action: 'navigate',
    destination: undefined,
    searchKey: undefined,
    volumeUpTrigger: false,
    preventSend: false,
    formatter: 'none',
    fontColor: undefined,

    // Input-specific properties
    inputType: 'string',
    minLength: undefined,
    maxLength: undefined,
    pattern: undefined,
    style: 'roundedBox',

    // Group-specific properties
    direction: 'column',
    distribution: 'leading',

    // Video-specific properties
    autoplay: false,
    loop: false,
    muted: false,
    controls: true,

    // Gallery-specific properties
    columns: 3,
    spacing: 20,

    // QR-specific properties
    qrSize: 200,
    qrColor: '#000000',
    qrBackgroundColor: '#ffffff',

    // Image-specific properties

    // Output preview properties
    rotation: 0,

    // Live view properties
    showControls: false,
    flowId: undefined,

    // Grid properties
    maximumSelectionCount: undefined,
    valueKey: undefined,
    displayTypes: undefined,

    // Group-specific properties

    // Missing properties from metadata interfaces
    key: undefined,
    label: undefined,
    source: undefined,
    optionShape: 'rectangle',
    optionBorderWidth: 0,
    optionBorderColor: '#000000',
    optionWidth: 100,
    optionHeight: 100,

    // Conditional rendering
    conditionalRendering: undefined,
};

interface ComprehensiveUIElementEditorProps {
    element: UIElement;
    screen: ScreenDefinition;
    onIdChange: (newId: string) => void;
}

interface PropertySection {
    title: string;
    icon: string;
    properties: string[];
}

const PROPERTY_SECTIONS: PropertySection[] = [
    {
        title: 'Layout',
        icon: '📐',
        properties: [
            'horizontalLocation',
            'verticalLocation',
            'groupLocation',
            'order',
            'layer',
        ],
    },
    {
        title: 'Behavior',
        icon: '⚡',
        properties: [
            'action',
            'destination',
            'value',
            'valueKey',
            'keyPressTrigger',
            'searchKey',
            'volumeUpTrigger',
            'preventSend',
            'formatter',
            'customPrice',
        ],
    },
    {
        title: 'Position',
        icon: '📍',
        properties: [
            'horizontalOffset',
            'verticalOffset',
            'horizontalPadding',
            'verticalPadding',
            'horizontalMargin',
            'verticalMargin',
        ],
    },
    {
        title: 'Appearance',
        icon: '🎨',
        properties: [
            'width',
            'height',
            'cornerRadius',
            'borderSize',
            'borderColor',
        ],
    },
    {
        title: 'Typography',
        icon: '✏️',
        properties: [
            'text',
            'fontSize',
            'fontWeight',
            'fontFace',
            'alignment',
            'hideWhenKeyboardVisible',
        ],
    },
    {
        title: 'Content',
        icon: '📄',
        properties: [
            'imageUrl',
            'inputType',
            'defaultValue',
            'label',
            'identifier',
            'placeholder',
            'scene',
            'useImageDimensions',
            'key',
            'source',
            'name',
        ],
    },
    {
        title: 'Colors',
        icon: '🌈',
        properties: [
            'foregroundColor',
            'backgroundColor',
            'selectionColor',
            'fontColor',
        ],
    },
    {
        title: 'Advanced Styling',
        icon: '✨',
        properties: [
            'borderRadius',
            'borderWidth',
            'opacity',
            'textAlign',
            'fontFamily',
            'textColor',
        ],
    },
    {
        title: 'Input Settings',
        icon: '📝',
        properties: [
            'placeholder',
            'minLength',
            'maxLength',
            'pattern',
            'isRequired',
            'isDisabled',
            'isHidden',
            'style',
            'borderSize',
            'borderColor',
            'isFocused',
        ],
    },
    {
        title: 'Media Properties',
        icon: '🎬',
        properties: [
            'videoUrl',
            'autoplay',
            'loop',
            'muted',
            'controls',
            'showControls',
            'flowId',
        ],
    },
    {
        title: 'Gallery Settings',
        icon: '🖼️',
        properties: [
            'columns',
            'spacing',
            'maximumSelectionCount',
            'valueKey',
            'displayTypes',
        ],
    },
    {
        title: 'Output Preview Settings',
        icon: '🖼️',
        properties: ['rotation', 'scene'],
    },
    {
        title: 'Artwork Selector',
        icon: '🎨',
        properties: [
            'source',
            'flowIds',
            'optionShape',
            'optionBorderWidth',
            'optionBorderColor',
            'optionWidth',
            'optionHeight',
        ],
    },

    {
        title: 'QR Code Settings',
        icon: '📱',
        properties: ['qrSize', 'qrColor', 'qrBackgroundColor'],
    },
    {
        title: 'Group Layout',
        icon: '📦',
        properties: [
            'name',
            'direction',
            'spacing',
            'alignment',
            'distribution',
        ],
    },
];

// Helper function to get nested property values
const getNestedProperty = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
};

export default function ComprehensiveUIElementEditor({
    element,
    screen,
    onIdChange,
}: ComprehensiveUIElementEditorProps) {
    const {
        replicateMode,
        setSourceScreenId,
        setSourceElementId,
        setShowReplicateDialog,
        selectedProperties,
    } = useReplicate();
    const dispatch = useDispatch();
    const screens = useSelector((state: any) => state.screens.present.entities);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['Layout', 'Behavior', 'Appearance']),
    );
    const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
    const [displayTypesDropdownOpen, setDisplayTypesDropdownOpen] =
        useState(false);
    const displayTypesButtonRef = useRef<HTMLButtonElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });

    // Calculate dropdown position
    const calculateDropdownPosition = () => {
        if (displayTypesButtonRef.current) {
            const rect = displayTypesButtonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    };

    const toggleSection = (sectionTitle: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionTitle)) {
            newExpanded.delete(sectionTitle);
        } else {
            newExpanded.add(sectionTitle);
        }
        setExpandedSections(newExpanded);
    };

    const updateElementProperty = (propertyPath: string, value: any) => {
        // Get the old value for undo support
        const oldValue = getNestedProperty(element, propertyPath);

        // Use undo-enabled property update
        updateElementPropertyWithUndo(
            dispatch,
            element.id,
            screen.id,
            propertyPath,
            value,
            oldValue,
            element.type,
            true, // debounced = true for form inputs
        );
    };

    const updateElementMetadataProperty = (
        propertyPath: string,
        value: any,
    ) => {
        // Get the old value for undo support
        const oldValue = getNestedProperty(element.metadata, propertyPath);
        const fullPropertyPath = `metadata.${propertyPath}`;

        // Use undo-enabled property update
        updateElementPropertyWithUndo(
            dispatch,
            element.id,
            screen.id,
            fullPropertyPath,
            value,
            oldValue,
            element.type,
            true, // debounced = true for form inputs
        );
    };

    const isPropertyAccessible = (propertyPath: string): boolean => {
        // If element is in a group, horizontal and vertical locations are not applicable
        if (
            element.groupLocation &&
            (propertyPath === 'horizontalLocation' ||
                propertyPath === 'verticalLocation')
        ) {
            return false;
        }

        // Check for useImageDimensions - if true, width/height are not applicable
        if (
            element.metadata &&
            (element.metadata as any).useImageDimensions &&
            (propertyPath === 'width' || propertyPath === 'height')
        ) {
            return false;
        }

        return true;
    };

    const getPropertyValue = (propertyPath: string): any => {
        let value: any;

        // Check if it's a direct element property
        if (
            [
                'horizontalLocation',
                'verticalLocation',
                'groupLocation',
                'order',
                'layer',
                'conditionalRendering',
            ].includes(propertyPath)
        ) {
            value = (element as any)[propertyPath];
        } else {
            // Check metadata, handling cases where metadata might be undefined
            value = element.metadata
                ? (element.metadata as any)[propertyPath]
                : undefined;
        }

        // Return default value if property is missing or undefined
        if (value === undefined || value === null) {
            return ELEMENT_PROPERTY_DEFAULTS[propertyPath];
        }

        return value;
    };

    const renderPropertyField = (propertyName: string) => {
        const value = getPropertyValue(propertyName);
        const isMetadataProperty = ![
            'horizontalLocation',
            'verticalLocation',
            'groupLocation',
            'order',
            'layer',
        ].includes(propertyName);

        const handleChange = (newValue: any) => {
            if (isMetadataProperty) {
                updateElementMetadataProperty(propertyName, newValue);
            } else {
                updateElementProperty(propertyName, newValue);
            }
        };

        // Helper function to render property with replicate checkbox
        const renderPropertyWithReplicate = (
            propertyName: string,
            label: string,
            inputElement: React.ReactNode,
        ) => (
            <div key={propertyName} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                        {label}
                    </label>
                    {replicateMode && (
                        <ReplicatePropertyCheckbox
                            property={propertyName}
                            label={label}
                        />
                    )}
                </div>
                {inputElement}
            </div>
        );

        // Skip properties that don't apply to this element type
        if (!isPropertyApplicable(propertyName, element.type, element)) {
            return null;
        }

        switch (propertyName) {
            case 'horizontalLocation':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Horizontal Position
                        </label>
                        {!isPropertyAccessible(propertyName) ? (
                            <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-500">
                                Not used (element is in a group)
                            </div>
                        ) : (
                            <select
                                value={value || 'center'}
                                onChange={(e) => handleChange(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="leading">Leading (Left)</option>
                                <option value="center">Center</option>
                                <option value="trailing">
                                    Trailing (Right)
                                </option>
                            </select>
                        )}
                    </div>
                );

            case 'verticalLocation':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Vertical Position
                        </label>
                        {!isPropertyAccessible(propertyName) ? (
                            <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-500">
                                Not used (element is in a group)
                            </div>
                        ) : (
                            <select
                                value={value || 'center'}
                                onChange={(e) => handleChange(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="top">Top</option>
                                <option value="center">Center</option>
                                <option value="bottom">Bottom</option>
                            </select>
                        )}
                    </div>
                );

            case 'groupLocation':
                // Find all group elements in the current screen
                const groupElements = (screen.elements || []).filter(
                    (el) => el.type === 'group',
                );

                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Group
                        </label>
                        <select
                            value={value || ''}
                            onChange={(e) => {
                                const newGroupId = e.target.value || undefined;
                                handleChange(newGroupId);

                                // When assigning to a group, clear individual positioning
                                if (newGroupId) {
                                    updateElementProperty(
                                        'horizontalLocation',
                                        undefined,
                                    );
                                    updateElementProperty(
                                        'verticalLocation',
                                        undefined,
                                    );
                                } else {
                                    // When removing from group, set default positioning
                                    updateElementProperty(
                                        'horizontalLocation',
                                        'center',
                                    );
                                    updateElementProperty(
                                        'verticalLocation',
                                        'center',
                                    );
                                }
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">
                                No Group (Position Individually)
                            </option>
                            {groupElements.map((group) => {
                                const groupName =
                                    (group.metadata as any)?.name || group.id;
                                return (
                                    <option key={group.id} value={group.id}>
                                        {groupName}
                                    </option>
                                );
                            })}
                        </select>
                        {groupElements.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                                No groups available. Add a Group element to
                                organize elements.
                            </p>
                        )}
                    </div>
                );

            case 'layer':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Layer (Z-Index)
                        </label>
                        <select
                            value={value || 0}
                            onChange={(e) =>
                                handleChange(parseInt(e.target.value))
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={0}>Layer 0 (Back)</option>
                            <option value={1}>Layer 1 (Middle)</option>
                            <option value={2}>Layer 2 (Front)</option>
                        </select>
                    </div>
                );

            case 'action':
                if (element.type !== 'button') return null;

                // Convert current value to array format for multi-select
                const currentActions = Array.isArray(value)
                    ? value
                    : value
                    ? [value]
                    : [];

                const availableActions = [
                    { value: 'navigate', label: 'Navigate' },
                    { value: 'reset', label: 'Reset' },
                    { value: 'share', label: 'Share' },
                    { value: 'print', label: 'Print' },
                    { value: 'gallery', label: 'Gallery' },
                    { value: 'shareAndReset', label: 'Share & Reset' },
                    {
                        value: 'shareAndClearGallery',
                        label: 'Share Gallery Selection & Clear Selection',
                    },
                    { value: 'shareGallery', label: 'Share Gallery Selection' },
                    {
                        value: 'clearGallerySelection',
                        label: 'Clear Gallery Selection',
                    },
                    { value: 'selectArtwork', label: 'Select Artwork' },
                    {
                        value: 'setNextProcessingFlow',
                        label: 'Set Next Processing Flow',
                    },
                    { value: 'capture', label: 'Capture' },
                    { value: 'multiSelectPrint', label: 'Multi Select Print' },
                    { value: 'setValue', label: 'Set Value' },
                    { value: 'searchShares', label: 'Search Shares' },
                ];

                const handleActionSelect = (actionValue: string) => {
                    const newActions = currentActions.includes(actionValue)
                        ? currentActions.filter((a) => a !== actionValue)
                        : [...currentActions, actionValue];

                    // Convert back to single string if only one action, otherwise keep as array
                    const newValue =
                        newActions.length === 1 ? newActions[0] : newActions;
                    handleChange(newValue);
                };

                const getDisplayText = () => {
                    if (currentActions.length === 0) {
                        return 'Select actions...';
                    }
                    if (currentActions.length === 1) {
                        const action = availableActions.find(
                            (a) => a.value === currentActions[0],
                        );
                        return action?.label || currentActions[0];
                    }
                    return `${currentActions.length} actions selected`;
                };

                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Button Actions
                        </label>

                        {/* Dropdown Button */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() =>
                                    setActionDropdownOpen(!actionDropdownOpen)
                                }
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                            >
                                <span
                                    className={`block truncate ${
                                        currentActions.length === 0
                                            ? 'text-gray-500'
                                            : 'text-gray-900'
                                    }`}
                                >
                                    {getDisplayText()}
                                </span>
                                <ChevronDown
                                    className={`w-4 h-4 text-black transition-transform ${
                                        actionDropdownOpen ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>

                            {/* Dropdown Menu */}
                            {actionDropdownOpen && (
                                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                    {availableActions.map((action) => {
                                        const isSelected =
                                            currentActions.includes(
                                                action.value,
                                            );
                                        return (
                                            <button
                                                key={action.value}
                                                type="button"
                                                onClick={() =>
                                                    handleActionSelect(
                                                        action.value,
                                                    )
                                                }
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                <div className="w-4 h-4 flex items-center justify-center">
                                                    {isSelected && (
                                                        <Check className="w-4 h-4 text-blue-600" />
                                                    )}
                                                </div>
                                                <span
                                                    className={`flex-1 ${
                                                        isSelected
                                                            ? 'text-blue-600 font-medium'
                                                            : 'text-gray-900'
                                                    }`}
                                                >
                                                    {action.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Selected actions list */}
                        {currentActions.length > 0 && (
                            <div className="mt-2">
                                <div className="text-xs font-medium text-gray-700 mb-1">
                                    Execution Order:
                                </div>
                                <div className="space-y-1">
                                    {currentActions.map(
                                        (actionValue, index) => {
                                            const action =
                                                availableActions.find(
                                                    (a) =>
                                                        a.value === actionValue,
                                                );
                                            return (
                                                <div
                                                    key={`${actionValue}-${index}`}
                                                    className="flex items-center justify-between px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <span className="w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                                            {index + 1}
                                                        </span>
                                                        <span className="text-blue-800 font-medium">
                                                            {action?.label ||
                                                                actionValue}
                                                        </span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleActionSelect(
                                                                actionValue,
                                                            )
                                                        }
                                                        className="p-0.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                                        title="Remove action"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'inputType':
                if (element.type !== 'input') return null;
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Input Type
                        </label>
                        <select
                            value={value || 'string'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="string">Text</option>
                            <option value="number">Number</option>
                            <option value="bool">Boolean</option>
                            <option value="phone">Phone</option>
                            <option value="email">Email</option>
                        </select>
                    </div>
                );

            case 'fontWeight':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Font Weight',
                    <select
                        value={value || 400}
                        onChange={(e) => handleChange(parseInt(e.target.value))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value={100}>Thin (100)</option>
                        <option value={200}>Extra Light (200)</option>
                        <option value={300}>Light (300)</option>
                        <option value={400}>Regular (400)</option>
                        <option value={500}>Medium (500)</option>
                        <option value={600}>Semi Bold (600)</option>
                        <option value={700}>Bold (700)</option>
                        <option value={800}>Extra Bold (800)</option>
                    </select>,
                );

            // Number inputs
            case 'order':
            case 'fontSize':
            case 'width':
            case 'height':
            case 'cornerRadius':
            case 'borderSize':
            case 'horizontalOffset':
            case 'verticalOffset':
            case 'horizontalPadding':
            case 'verticalPadding':
            case 'horizontalMargin':
            case 'verticalMargin':
            case 'spacing':
            case 'columns':
            case 'optionBorderWidth':
            case 'optionWidth':
            case 'optionHeight':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <input
                        type="number"
                        value={value || 0}
                        onChange={(e) =>
                            handleChange(parseInt(e.target.value) || 0)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                    />,
                );

            case 'maximumSelectionCount':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Max Selection Count',
                    <input
                        type="number"
                        value={value || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            // If empty or 0, set to undefined (unlimited)
                            handleChange(
                                val === '' || val === '0'
                                    ? undefined
                                    : parseInt(val),
                            );
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="unlimited"
                        min="1"
                    />,
                );

            case 'displayTypes':
                if (!['galleryGrid', 'mediaGrid'].includes(element.type))
                    return null;

                // Convert current value to array format for multi-select
                const currentDisplayTypes = Array.isArray(value) ? value : [];

                const availableDisplayTypes = [
                    { value: 'video', label: 'Video' },
                    { value: 'photo', label: 'Photo' },
                    { value: 'print', label: 'Print' },
                    { value: 'user-edit', label: 'User Edit' },
                ];

                const handleDisplayTypeSelect = (typeValue: string) => {
                    const newTypes = currentDisplayTypes.includes(typeValue)
                        ? currentDisplayTypes.filter((t) => t !== typeValue)
                        : [...currentDisplayTypes, typeValue];

                    // Set to undefined if empty, otherwise keep as array
                    const newValue =
                        newTypes.length === 0 ? undefined : newTypes;
                    handleChange(newValue);
                };

                const getDisplayTypesText = () => {
                    if (currentDisplayTypes.length === 0) {
                        return 'All media types (default)';
                    }
                    if (currentDisplayTypes.length === 1) {
                        const type = availableDisplayTypes.find(
                            (t) => t.value === currentDisplayTypes[0],
                        );
                        return type?.label || currentDisplayTypes[0];
                    }
                    return `${currentDisplayTypes.length} types selected`;
                };

                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Display Types
                        </label>

                        <div className="space-y-2">
                            {/* Dropdown Button */}
                            <button
                                ref={displayTypesButtonRef}
                                type="button"
                                onClick={() => {
                                    if (!displayTypesDropdownOpen) {
                                        calculateDropdownPosition();
                                    }
                                    setDisplayTypesDropdownOpen(
                                        !displayTypesDropdownOpen,
                                    );
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                            >
                                <span
                                    className={`block truncate ${
                                        currentDisplayTypes.length === 0
                                            ? 'text-gray-500'
                                            : 'text-gray-900'
                                    }`}
                                >
                                    {getDisplayTypesText()}
                                </span>
                                <ChevronDown
                                    className={`w-4 h-4 text-black transition-transform ${
                                        displayTypesDropdownOpen
                                            ? 'rotate-180'
                                            : ''
                                    }`}
                                />
                            </button>

                            {/* Selected types display */}
                            {currentDisplayTypes.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {currentDisplayTypes.map((typeValue) => {
                                        const type = availableDisplayTypes.find(
                                            (t) => t.value === typeValue,
                                        );
                                        return (
                                            <span
                                                key={typeValue}
                                                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800"
                                            >
                                                {type?.label || typeValue}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleDisplayTypeSelect(
                                                            typeValue,
                                                        )
                                                    }
                                                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-green-200"
                                                    title="Remove type"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Portal-based Dropdown Menu */}
                        {displayTypesDropdownOpen &&
                            createPortal(
                                <>
                                    {/* Backdrop to close dropdown */}
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() =>
                                            setDisplayTypesDropdownOpen(false)
                                        }
                                    />

                                    {/* Dropdown menu */}
                                    <div
                                        className="absolute z-50 bg-white border border-gray-300 rounded-md shadow-lg"
                                        style={{
                                            top: dropdownPosition.top,
                                            left: dropdownPosition.left,
                                            width: dropdownPosition.width,
                                        }}
                                    >
                                        {availableDisplayTypes.map((type) => {
                                            const isSelected =
                                                currentDisplayTypes.includes(
                                                    type.value,
                                                );
                                            return (
                                                <button
                                                    key={type.value}
                                                    type="button"
                                                    onClick={() =>
                                                        handleDisplayTypeSelect(
                                                            type.value,
                                                        )
                                                    }
                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                                >
                                                    <div className="w-4 h-4 flex items-center justify-center">
                                                        {isSelected && (
                                                            <Check className="w-4 h-4 text-blue-600" />
                                                        )}
                                                    </div>
                                                    <span
                                                        className={`flex-1 ${
                                                            isSelected
                                                                ? 'text-blue-600 font-medium'
                                                                : 'text-gray-900'
                                                        }`}
                                                    >
                                                        {type.label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>,
                                document.body,
                            )}
                    </div>
                );

            case 'imageUrl':
                return (
                    <URLInputWithLibrary
                        key={propertyName}
                        value={value}
                        onChange={handleChange}
                        placeholder={getPropertyPlaceholder(propertyName)}
                        label={formatPropertyName(propertyName)}
                    />
                );

            case 'fontFace':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <FontDropdown
                        value={value as string}
                        onChange={(value) => handleChange(value)}
                        placeholder="Select a font..."
                        className="w-full"
                    />,
                );

            case 'destination':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Destination Screen',
                    <select
                        value={value || ''}
                        onChange={(e) =>
                            handleChange(e.target.value || undefined)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select destination screen...</option>
                        {Object.values(screens)
                            .filter(
                                (screenOption: any) =>
                                    screenOption.id !== screen.id,
                            ) // Don't show current screen
                            .map((screenOption: any) => (
                                <option
                                    key={screenOption.id}
                                    value={screenOption.id}
                                >
                                    {screenOption.id}
                                </option>
                            ))}
                    </select>,
                );

            // Text inputs
            case 'text':
            case 'value':
            case 'valueKey':
            case 'keyPressTrigger':
            case 'defaultValue':
            case 'label':
            case 'identifier':
            case 'placeholder':
            case 'scene':
            case 'key':
            case 'source':
            case 'name':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={getPropertyPlaceholder(propertyName)}
                    />,
                );

            // Color inputs
            case 'borderColor':
            case 'foregroundColor':
            case 'backgroundColor':
            case 'selectionColor':
            case 'optionBorderColor':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <div className="flex gap-2">
                        <input
                            type="color"
                            value={value || '#000000'}
                            onChange={(e) => handleChange(e.target.value)}
                            className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => handleChange(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="#000000"
                        />
                    </div>,
                );

            case 'useImageDimensions':
            case 'isFocused':
            case 'showControls':
            case 'volumeUpTrigger':
            case 'preventSend':
            case 'hideWhenKeyboardVisible':
                return renderPropertyWithReplicate(
                    propertyName,
                    formatPropertyName(propertyName),
                    <label className="flex items-center text-xs font-medium text-gray-700">
                        <input
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={(e) => handleChange(e.target.checked)}
                            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {formatPropertyName(propertyName)}
                    </label>,
                );

            case 'flowId':
                return (
                    <div key={propertyName} className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Processing Flow
                        </label>
                        <FlowSelector
                            value={value || undefined}
                            onChange={(newValue) =>
                                handleChange(
                                    Array.isArray(newValue)
                                        ? newValue[0]
                                        : newValue,
                                )
                            }
                            multiple={false}
                            placeholder="Select a processing flow..."
                        />
                    </div>
                );

            case 'flowIds':
                return (
                    <div key={propertyName} className="mb-3">
                        <FlowSelector
                            value={Array.isArray(value) ? value : []}
                            onChange={(newValue) =>
                                handleChange(
                                    Array.isArray(newValue)
                                        ? newValue
                                        : [newValue],
                                )
                            }
                            multiple={true}
                            label="Processing Flows"
                            placeholder="Select processing flows..."
                        />
                    </div>
                );

            case 'optionShape':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Option Shape',
                    <select
                        value={value || 'rectangle'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="circle">Circle</option>
                        <option value="rectangle">Rectangle</option>
                        <option value="roundedRectangle">
                            Rounded Rectangle
                        </option>
                    </select>,
                );

            case 'alignment':
                // Different labels based on element type
                const isGroupElement = element.type === 'group';
                const alignmentLabel = isGroupElement
                    ? 'Child Alignment'
                    : 'Text Alignment';

                return renderPropertyWithReplicate(
                    propertyName,
                    alignmentLabel,
                    <select
                        value={value || 'center'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="leading">Leading (Left)</option>
                        <option value="center">Center</option>
                        <option value="trailing">Trailing (Right)</option>
                    </select>,
                );

            case 'direction':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Group Direction',
                    <select
                        value={value || 'column'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="column">Column (Vertical)</option>
                        <option value="row">Row (Horizontal)</option>
                    </select>,
                );

            case 'style':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Input Style',
                    <select
                        value={value || 'roundedBox'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="roundedBox">Rounded Box</option>
                        <option value="line">Line</option>
                    </select>,
                );

            case 'formatter':
                return renderPropertyWithReplicate(
                    propertyName,
                    'Formatter',
                    <select
                        value={value || 'none'}
                        onChange={(e) => handleChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="none">None</option>
                        <option value="email">Email</option>
                        <option value="phoneNumber">Phone Number</option>
                    </select>,
                );

            case 'customPrice':
                // Convert cents to dollars for display, and dollars back to cents for storage
                const priceInDollars =
                    value !== undefined && value !== null
                        ? (value / 100).toFixed(2)
                        : '';
                return (
                    <div key={propertyName} className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-medium text-gray-700">
                                Custom Price (USD)
                            </label>
                            {replicateMode && (
                                <ReplicatePropertyCheckbox
                                    property={propertyName}
                                    label="Custom Price"
                                />
                            )}
                        </div>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                $
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={priceInDollars}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (
                                        inputValue === '' ||
                                        inputValue === null
                                    ) {
                                        // Clear the value to use default
                                        handleChange(undefined);
                                    } else {
                                        const dollars =
                                            parseFloat(inputValue) || 0;
                                        const cents = Math.round(dollars * 100);
                                        handleChange(cents);
                                    }
                                }}
                                placeholder="0.00"
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                            Leave empty to use default activation price
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const isPropertyApplicable = (
        propertyName: string,
        elementType: UIElementType,
        elementData?: UIElement,
    ): boolean => {
        const typePropertyMap: { [key in UIElementType]: string[] } = {
            text: [
                'text',
                'fontSize',
                'fontWeight',
                'fontFace',
                'horizontalOffset',
                'verticalOffset',
                'horizontalPadding',
                'verticalPadding',
                'horizontalMargin',
                'verticalMargin',
                'hideWhenKeyboardVisible',
                'alignment',
            ],
            dynamicText: [
                'identifier',
                'text',
                'key',
                'fontSize',
                'fontWeight',
                'fontFace',
                'horizontalMargin',
                'verticalMargin',
                'hideWhenKeyboardVisible',
                'alignment',
                'horizontalOffset',
                'verticalOffset',
                'horizontalPadding',
                'verticalPadding',
            ],
            button: [
                'text',
                'fontSize',
                'fontWeight',
                'fontFace',
                'imageUrl',
                'borderSize',
                'borderColor',
                'horizontalPadding',
                'verticalPadding',
                'horizontalMargin',
                'verticalMargin',
                'cornerRadius',
                'action',
                'destination',
                'value',
                'valueKey',
                'keyPressTrigger',
                'volumeUpTrigger',
                'searchKey',
                'preventSend',
                'formatter',
                'hideWhenKeyboardVisible',
                'fontColor',
                'backgroundColor',
                'width',
                'height',
            ],
            image: [
                'imageUrl',
                'width',
                'height',
                'cornerRadius',
                'horizontalOffset',
                'verticalOffset',
                'useImageDimensions',
            ],
            input: [
                'inputType',
                'label',
                'cornerRadius',
                'defaultValue',
                'fontSize',
                'fontWeight',
                'fontFace',
                'key',
                'width',
                'isFocused',
                'style',
                'placeholder',
                'alignment',
                'horizontalPadding',
                'verticalPadding',
                'borderSize',
                'borderColor',
            ],
            currentSessionQR: [
                'width',
                'height',
                'horizontalPadding',
                'verticalPadding',
                'horizontalOffset',
                'verticalOffset',
                'foregroundColor',
                'backgroundColor',
            ],
            paymentQR: [
                'width',
                'height',
                'horizontalPadding',
                'verticalPadding',
                'horizontalOffset',
                'verticalOffset',
                'horizontalMargin',
                'verticalMargin',
                'foregroundColor',
                'backgroundColor',
                'destination',
                'customPrice',
                'fontFace',
                'fontSize',
                'fontWeight',
            ],
            outputPreview: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'cornerRadius',
                'scene',
                'rotation',
            ],
            galleryGrid: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'selectionColor',
                'columns',
                'spacing',
                'cornerRadius',
                'maximumSelectionCount',
                'valueKey',
                'displayTypes',
                'horizontalMargin',
                'verticalMargin',
            ],
            mediaGrid: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'selectionColor',
                'columns',
                'spacing',
                'cornerRadius',
                'maximumSelectionCount',
                'valueKey',
                'displayTypes',
                'horizontalMargin',
                'verticalMargin',
            ],
            liveView: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'cornerRadius',
                'showControls',
                'flowId',
            ],
            selectedQR: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'foregroundColor',
                'backgroundColor',
                'horizontalMargin',
                'verticalMargin',
            ],
            group: [
                'groupLocation',
                'direction',
                'spacing',
                'alignment',
                'name',
            ],
            artworkSelector: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'source',
                'flowIds',
                'optionShape',
                'optionBorderWidth',
                'optionBorderColor',
                'optionWidth',
                'optionHeight',
                'cornerRadius',
            ],
            sessionGrid: [
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'selectionColor',
                'columns',
                'spacing',
                'cornerRadius',
                'maximumSelectionCount',
                'valueKey',
                'horizontalMargin',
                'verticalMargin',
            ],
            codeScanner: [],
            phoneKeypad: [
                'key',
                'width',
                'height',
                'horizontalOffset',
                'verticalOffset',
                'fontSize',
                'fontWeight',
                'fontFace',
                'buttonShape',
                'buttonBorderWidth',
                'buttonBorderRadius',
                'buttonBorderColor',
                'buttonFontFace',
                'buttonFontSize',
                'buttonFontWeight',
                'buttonBackgroundColor',
                'buttonTextColor',
                'buttonHorizontalPadding',
                'buttonVerticalPadding',
            ],
        };

        const commonProperties = [
            'horizontalLocation',
            'verticalLocation',
            'order',
            'layer',
            'conditionalRendering',
        ];
        const applicableProperties = [
            ...commonProperties,
            ...(typePropertyMap[elementType] || []),
        ];

        // Hide width and height when useImageDimensions is true
        if (
            elementData &&
            (propertyName === 'width' || propertyName === 'height')
        ) {
            const metadata = elementData.metadata as any;
            if (metadata && metadata.useImageDimensions === true) {
                return false;
            }
        }

        return applicableProperties.includes(propertyName);
    };

    const formatPropertyName = (propertyName: string): string => {
        return propertyName
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const getPropertyPlaceholder = (propertyName: string): string => {
        const placeholders: { [key: string]: string } = {
            text: 'Enter text content...',
            imageUrl: 'https://example.com/image.jpg',
            destination: 'screen-id',
            value: 'value to set',
            valueKey: 'variable name',
            keyPressTrigger: 'Enter, Space, etc.',
            defaultValue: 'default input value',
            label: 'Input label',
            identifier: 'dynamic-text-id',
            fontFace: 'Roboto-Regular, Poppins-Bold, etc.',
            groupLocation: 'group-name',
            source: 'source-screen-id',
            columns: '3',
            maximumSelectionCount: 'unlimited (leave empty)',
            spacing: '20',
        };
        return placeholders[propertyName] || '';
    };

    return (
        <div className="space-y-4 mb-24">
            {/* Element ID Field */}
            <div className="p-3 bg-gray-50 rounded-lg border">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Element ID
                </label>
                <input
                    type="text"
                    value={element.id}
                    onChange={(e) => onIdChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Element Type Badge */}
            <div className="flex items-center justify-between gap-2 p-2 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-blue-100 rounded text-xs font-medium text-blue-700">
                        {element.type}
                    </div>
                    <span className="text-xs text-blue-600">
                        {formatPropertyName(element.type)} Element
                    </span>
                </div>
                {replicateMode && (
                    <button
                        onClick={() => {
                            setSourceScreenId(screen.id);
                            setSourceElementId(element.id);
                            setShowReplicateDialog(true);
                        }}
                        disabled={selectedProperties.length === 0}
                        className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Replicate ({selectedProperties.length})
                    </button>
                )}
            </div>

            {/* Property Sections */}
            {PROPERTY_SECTIONS.map((section) => {
                let applicableProperties = section.properties.filter((prop) =>
                    isPropertyApplicable(prop, element.type, element),
                );

                // Special handling: Remove alignment from Typography section for group elements
                if (
                    section.title === 'Typography' &&
                    element.type === 'group'
                ) {
                    applicableProperties = applicableProperties.filter(
                        (prop) => prop !== 'alignment',
                    );
                }

                if (applicableProperties.length === 0) return null;

                const isExpanded = expandedSections.has(section.title);

                return (
                    <div
                        key={section.title}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                        <button
                            onClick={() => toggleSection(section.title)}
                            className="w-full max-w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors overflow-hidden"
                        >
                            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-[calc(100%-3rem)]">
                                <span className="text-sm flex-shrink-0">
                                    {section.icon}
                                </span>
                                <span className="text-sm font-medium text-gray-900 truncate">
                                    {section.title}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                    ({applicableProperties.length})
                                </span>
                            </div>
                            <div className="flex-shrink-0 w-8 h-6 flex items-center justify-center pr-2">
                                {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                            </div>
                        </button>

                        {isExpanded && (
                            <div className="p-3 border-t border-gray-200 bg-gray-50">
                                {applicableProperties.map((propertyName) =>
                                    renderPropertyField(propertyName),
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Conditional Rendering Section */}
            <div className="border border-gray-200 rounded-lg">
                <div className="p-3 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                        🎯 Conditional Rendering
                    </h4>
                    <div className="text-xs text-gray-600 mb-3">
                        Show this element only when certain conditions are met
                    </div>

                    {element.conditionalRendering ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-700">
                                    Condition:
                                </span>
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                    {element.conditionalRendering.type}
                                </span>
                            </div>
                            <button
                                onClick={() =>
                                    updateElementProperty(
                                        'conditionalRendering',
                                        undefined,
                                    )
                                }
                                className="text-xs text-red-600 hover:text-red-700"
                            >
                                Remove condition
                            </button>
                        </div>
                    ) : (
                        <select
                            onChange={(e) => {
                                if (e.target.value) {
                                    updateElementProperty(
                                        'conditionalRendering',
                                        {
                                            type: e.target.value,
                                            metadata: {},
                                        },
                                    );
                                }
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Always Visible</option>
                            <option value="hasPrinter">Has Printer</option>
                            <option value="hasEmailSharingEnabled">
                                Email Sharing Enabled
                            </option>
                            <option value="hasTextSharingEnabled">
                                Text Sharing Enabled
                            </option>
                            <option value="hasSelectedMedia">
                                Has Selected Media
                            </option>
                        </select>
                    )}
                </div>
            </div>
        </div>
    );
}
